<template>
  <div class="container-fluid admin-page">
    <div class="title-area">
      <h2>付箋管理</h2>
    </div>
    <div class="search-area">
      <form class="search-form" v-on:submit.prevent>
        <div class="form-group">
          <label class="form-control-label">期間</label>
          <div>
            <MyCalendar
              class="date-from"
              :date-value="searchState.search.dtFrom"
              @date-input="searchState.search.dtFrom = $event"
            />
            <span class="ib waveline ta-center ml4 mr4">〜</span>
            <MyCalendar
              class="date-to"
              :date-value="searchState.search.dtTo"
              @date-input="searchState.search.dtTo = $event"
            />
          </div>
        </div>
        <div class="form-group">
          <label class="form-control-label group2">種別</label>
          <select class="form-control comment-type" v-model="searchState.search.commentType">
            <option :value="null"></option>
            <option v-for="e in masters.comment.commentTypes" :key="e.commentType" :value="e.commentType">
              {{e.commentTypeDisp}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label group2">公開範囲</label>
          <select class="form-control share-scope" v-model="searchState.search.shareScope">
            <option :value="null"></option>
            <option v-for="e in masters.lov.shareScopes" :key="e.key" :value="e.key">
              {{e.valAlt}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label group2">作成者</label>
          <select class="form-control created-by" v-model="searchState.search.userId">
            <option :value="null"></option>
            <option v-for="e in searchState.users" :key="e.id" :value="e.id">
              {{e.displayName}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label keyword">キーワード</label>
          <input
            type="text"
            class="form-control keyword"
            v-model="searchState.search.content"
          >
        </div>
        <div class="form-group search-btn ta-center">
          <button
            type="submit"
            class="btn btn-primary"
            @click="doSearch"
          >検索</button>
        </div>
      </form>
    </div>
    <AdminListTable
      :display-data="displayData"
      :fields="fields"
      @start-edit="edit"
      @start-destroy="tryDestroy"
    />

    <CommentEditModal
      v-if="editState.showEditModal && editState.editingComment"
      :editing-comment="editState.editingComment"
      :is-requesting="editState.isRequesting"
      :http-error-msgs="editState.httpErrorMsgs"
      @update="onUpdateComment"
      @close="editState.showEditModal = false"
    />
    <Modal
      v-if="editState.showDestroyModal"
      :title="destroyModalTitle"
      :show-bottom-dismiss-button="true"
      @close="doDestroy"
      @dismiss="editState.showDestroyModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p2 font-mtx-desc" v-html="destroyModalMsg"></p>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="editState.showErrorModal"
      :title="errorModalTitle"
      :show-bottom-dismiss-button="false"
      @close="editState.showErrorModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p2 font-mtx-desc" v-html="errorModalMsg"></p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import commentApi from 'src/apis/comment';
import userApi from 'src/apis/user';
import { computed, onMounted, reactive, type ComputedRef } from 'vue';
import type { Comment } from 'src/models/comment';
import type { AdminCommentUpdateRequest } from 'src/models/apis/comment/adminCommentRequest';
import type { AdminUser } from 'src/models/user';
import type { AdminListTableField } from 'src/models/adminListTable';
import { useReactiveMasters } from 'src/composables/useMasters';
import { getLocationDispOfKp } from 'src/lib/commentHelper';
import { lineBreakToBR, splitByLineBreak } from 'src/lib/utils';
import { dtFormat } from 'src/lib/dateHelper';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';
import CommentEditModal from 'src/components/Admin/Comment/CommentEditModal.vue';

interface CommentSearchParams {
  commentType: string | null;
  userId: number | null;
  dtFrom: Date;
  dtTo: Date;
  content: string | null;
  shareScope: number | null;
}

type CommentDisp = Comment & {
  tsDisp: string;
  contentDisp: string;
};

interface AdminCommentsTableRow extends CommentDisp {
  commentTypeDisp: string;
  shareScopeLabel: string;
  kpInfo?: string;
  angleDisp?: string;
  hasFile: string;
}

interface CommentsSearchState {
  search: CommentSearchParams;
  filteredComments: CommentDisp[];
  users: AdminUser[];
}

interface CommentsEditState {
  showEditModal: boolean;
  editingComment: Comment | null;
  isRequesting: boolean;
  httpErrorMsgs: string[];
  showDestroyModal: boolean;
  destroyingComment: Comment | null;
  showErrorModal: boolean;
}

const now = new Date();
const initSearchState = (): CommentSearchParams => {
  return {
    commentType: null,
    userId: null,
    dtFrom: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    dtTo: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    content: null,
    shareScope: null,
  };
};

const searchState = reactive<CommentsSearchState>({
  search: initSearchState(),
  filteredComments: [],
  users: [],
});
const editState = reactive<CommentsEditState>({
  showEditModal: false,
  editingComment: null,
  isRequesting: false,
  httpErrorMsgs: [],
  showDestroyModal: false,
  destroyingComment: null,
  showErrorModal: false,
});
const { masters, resolveMastersPromise } = useReactiveMasters();

const convComments = (comments: Comment[]): CommentDisp[] => {
  return comments.map<CommentDisp>(e => {
    return {
      ...e,
      tsDisp: dtFormat(e.ts),
      content: splitByLineBreak(e.content).join('\n'),
      contentDisp: lineBreakToBR(e.content),
    };
  });
};
const doSearch = async(): Promise<void> => {
  searchState.filteredComments = [];
  const params = searchState.search;
  const comments = await commentApi.adminIndex({
    dt_from: params.dtFrom,
    dt_to: params.dtTo ? new Date(params.dtTo.valueOf() + 86400 * 1000) : params.dtTo,
    user_id: params.userId,
    share_scope: params.shareScope,
    comment_type: params.commentType,
    content: params.content,
  });
  if (!comments || comments.length === 0) return;

  searchState.filteredComments = convComments(comments);
};

const getCommentTypeDisp = (type: string): string => {
  for (const commentType of masters.comment.commentTypes) {
    if (commentType.commentType === type) {
      return commentType.commentTypeDisp;
    }
  }
  return type;
};
const getShareScopeLabel = (scope: number): string => {
  for (const shareScope of masters.lov.shareScopes) {
    if (shareScope.key === scope) {
      return shareScope.val;
    }
  }
  return scope.toString();
};
const getKpInfo = (kpUid: string | null): string | undefined => {
  if (!kpUid) return;

  const kps = Array.from(masters.kp.kpMap.values())
    .map(e => Array.from(e.values())).flat(2);
  const kpObj = kps.find(e => e.kpUid === kpUid);
  if (!kpObj) return;

  return getLocationDispOfKp(kpObj, masters.kp.roadNameDispMap);
};

const displayData: ComputedRef<AdminCommentsTableRow[]> = computed(() => {
  return searchState.filteredComments.map(comment => {
    return {
      ...comment,
      commentTypeDisp: getCommentTypeDisp(comment.commentType),
      shareScopeLabel: getShareScopeLabel(comment.shareScope),
      kpInfo: getKpInfo(comment.kpUid),
      angleDisp: comment.angle === null || comment.angle === undefined ? '' : `${comment.angle}°`,
      hasFile: comment.commentFiles.length > 0 ? '有' : '',
    };
  });
});

const fields: AdminListTableField<AdminCommentsTableRow>[] = [
  { name: 'tsDisp', label: '最終更新日時' },
  { name: 'commentTypeDisp', label: '種別' },
  { name: 'kpInfo', label: '場所' },
  { name: 'angleDisp', label: '方角' },
  { name: 'shareScopeLabel', label: '公開範囲' },
  { name: 'userDisplayName', label: '作成者' },
  { name: 'contentDisp', label: '内容', type: 'html' },
  { name: 'hasFile', label: '添付' },
];

const edit = (obj: Comment): void => {
  editState.editingComment = obj;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const onUpdateComment = async(id: number, payload: AdminCommentUpdateRequest): Promise<void> => {
  editState.isRequesting = true;
  try {
    await commentApi.adminUpdate(id, payload);
    editState.httpErrorMsgs = [];
    editState.showEditModal = false;
    await doSearch();
  } catch (err) {
    editState.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    editState.isRequesting = false;
  }
};
const tryDestroy = (obj: Comment): void => {
  editState.destroyingComment = obj;
  editState.showDestroyModal = true;
};
const doDestroy = async(): Promise<void> => {
  if (!editState.destroyingComment) return;

  try {
    await commentApi.adminDestroy(editState.destroyingComment.id);
    editState.showDestroyModal = false;
    await doSearch();
  } catch {
    editState.showDestroyModal = false;
    editState.showErrorModal = true;
  }
};

onMounted(async() => {
  const [, users] = await Promise.all([
    resolveMastersPromise(),
    userApi.adminIndex({}),
  ]);
  searchState.users = users;
  await doSearch();
});

const destroyModalTitle = '削除';
const destroyModalMsg = 'この付箋を削除してもよろしいですか？';
const errorModalTitle = 'エラー';
const errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';
</script>

<style lang="scss"  scoped>
.search-area {
  .comment-type.form-control {
    width: 102px !important;
  }
  .created-by.form-control {
    width: 148px !important;
  }
  .share-scope.form-control {
    width: 102px !important;
  }
  .keyword.form-control {
    width: 120px !important;
  }
}
</style>
